import React from "react";

import { PLAN_PERMISSIONS_NAMES as PLAN_PERMISSION_NAMES } from "../redux/modules/auth/userPermissions/selector";
import ApiClientAsync from "../api/ApiClientAsync";

const analysisCategoriesCache = new Map();

const cacheKeyFor = ({ market, ticker, userId, insiderAllowed }) =>
  [
    String(userId ?? ""),
    String(market).toLowerCase(),
    String(ticker).toLowerCase(),
    insiderAllowed ? "1" : "0"
  ].join(":");

const insiderPermissionNames = [
  PLAN_PERMISSION_NAMES.INSIDER_ANALYSES,
  PLAN_PERMISSION_NAMES.INSIDER_RECOMMENDATIONS
];

const hasInsiderAnalysesPermission = hasPermission =>
  Boolean(
    typeof hasPermission === "function" &&
      hasPermission(insiderPermissionNames)
  );

export default function withStockAnalysesCategories(WrappedComponent) {
  class WithStockAnalysesCategories extends React.Component {
    state = {
      stockPageAnalysisCategories: [],
      stockPageInsiderAnalysisCategories: [],
      analysisCategoriesLoading: true
    };
    async componentDidMount() {
      await this.initState();
    }

    async initState() {
      const requestId = (this._requestId || 0) + 1;
      this._requestId = requestId;
      const { hasPermission, stock, userID } = this.props;
      const { ticker, market } = stock?.stock ?? {};

      // Stay loading until stock identity is known; otherwise an empty
      // result is treated as "section missing" and the section page pops.
      if (!ticker || !market) {
        this.setState({ analysisCategoriesLoading: true });
        return;
      }

      const insiderAllowed = hasInsiderAnalysesPermission(hasPermission);
      const cacheKey = cacheKeyFor({
        market,
        ticker,
        userId: userID,
        insiderAllowed
      });
      const cached = analysisCategoriesCache.get(cacheKey);

      if (cached) {
        this.setState({
          stockPageAnalysisCategories: cached.stockPageAnalysisCategories,
          stockPageInsiderAnalysisCategories:
            cached.stockPageInsiderAnalysisCategories,
          analysisCategoriesLoading: false
        });
        return;
      }

      this.setState({ analysisCategoriesLoading: true });

      const publicResult = await this.getAnalysisCategories(
        "app.stocks.stockAnalysesFilteredByCategory"
      );
      const insiderResult = insiderAllowed
        ? await this.getAnalysisCategories(
            "app.stocks.insiderAnalysesFilteredByCategory"
          )
        : { ok: true, categories: [] };

      if (requestId !== this._requestId) {
        return;
      }

      const stockPageAnalysisCategories = publicResult.categories;
      const stockPageInsiderAnalysisCategories = insiderResult.categories;

      // Cache only successful fetches. The key includes user and insider
      // permission, so a later login or permission grant does not reuse them.
      if (publicResult.ok && insiderResult.ok) {
        analysisCategoriesCache.set(cacheKey, {
          stockPageAnalysisCategories,
          stockPageInsiderAnalysisCategories
        });
      }

      this.setState({
        stockPageAnalysisCategories,
        stockPageInsiderAnalysisCategories,
        analysisCategoriesLoading: false
      });
    }

    async getAnalysisCategories(urlPath) {
      const { stock, locale, authToken } = this.props;
      const apiClient = new ApiClientAsync(locale, authToken);

      const { ticker, market } = stock?.stock ?? {};

      if (!market || !ticker) {
        return { ok: false, categories: [] };
      }

      try {
        const response = await apiClient.get({
          urlPath,
          variables: {
            ticker,
            market
          }
        });

        if (!Array.isArray(response)) {
          return { ok: false, categories: [] };
        }

        return {
          ok: true,
          categories: response.filter(analyse => analyse?.analysis_count > 0)
        };
      } catch (e) {
        console.error(e);
        return { ok: false, categories: [] };
      }
    }

    async componentDidUpdate(prevProps) {
      const { stock, userID, hasPermission } = this.props;
      const { ticker, market } = stock?.stock ?? {};
      const prevTicker = prevProps?.stock?.stock?.ticker;
      const prevMarket = prevProps?.stock?.stock?.market;
      const stockChanged = ticker !== prevTicker || market !== prevMarket;
      const userChanged = userID !== prevProps.userID;
      const insiderAllowed = hasInsiderAnalysesPermission(hasPermission);
      const prevInsiderAllowed = hasInsiderAnalysesPermission(
        prevProps.hasPermission
      );

      if (
        !ticker ||
        !market ||
        (!stockChanged && !userChanged && insiderAllowed === prevInsiderAllowed)
      ) {
        return;
      }

      await this.initState();
    }

    render() {
      const {
        stockPageAnalysisCategories,
        stockPageInsiderAnalysisCategories,
        analysisCategoriesLoading
      } = this.state;

      return (
        <WrappedComponent
          {...this.props}
          analysisCategories={stockPageAnalysisCategories}
          insiderAnalysisCategories={stockPageInsiderAnalysisCategories}
          analysisCategoriesLoading={analysisCategoriesLoading}
        />
      );
    }
  }

  return WithStockAnalysesCategories;
}
